import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  signal,
  inject,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TreeModule } from 'primeng/tree';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TreeNode, TreeDragDropService, MessageService } from 'primeng/api';
import { TreeNodeDropEvent } from 'primeng/tree';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { ClassifyModel } from '../../models/classify.model';
import { ClassifyService } from '../../services/classify.service';
import { getCategoryDisplayName } from '@shared/utils/treeselect.util';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

@Component({
  selector: 'app-classify-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TreeModule,
    InputTextModule,
    ButtonModule,
    TranslateModule,
    TooltipModule,
    AppButtonComponent,
  ],
  providers: [TreeDragDropService],
  templateUrl: './classify-sidebar.component.html',
  styleUrls: ['./classify-sidebar.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClassifySidebarComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private classifyService = inject(ClassifyService);
  private cdr = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);
  // Resolves to classify-list's own MessageService instance (it declares its own `providers:
  // [MessageService]`), which is what the <p-toast> on this page actually listens to - do not
  // swap this for the app-wide ErrorHandlerService, whose root-scoped MessageService is a
  // different instance and would silently show no toast at all.
  private messageService = inject(MessageService);
  // Tree data
  treeData = signal<TreeNode[]>([]);
  filteredTreeData = signal<TreeNode[]>([]);
  selectedNode = signal<TreeNode | null>(null);
  loading = signal(false);
  searchText = signal('');

  // Output events - only categorySelect
  @Output() categorySelect = new EventEmitter<ClassifyModel | null>();

  // Input to clear selection when search form is used
  @Input() clearSelection = false;

  @ViewChild('treeContainer') treeContainerRef?: ElementRef<HTMLElement>;
  // Bound once and added/removed in capture phase (see ngAfterViewInit) so it still fires even
  // if PrimeNG's own drag handlers call stopPropagation() on the bubble phase.
  private readonly onTreeDragOver = (event: DragEvent): void => this.autoScrollTreeOnDragOver(event);

  constructor() {
  }

  ngAfterViewInit(): void {
    this.treeContainerRef?.nativeElement.addEventListener('dragover', this.onTreeDragOver, true);
  }

  ngOnDestroy(): void {
    this.treeContainerRef?.nativeElement.removeEventListener('dragover', this.onTreeDragOver, true);
  }

  /** Native HTML5 drag-and-drop (used by PrimeNG's `p-tree` draggableNodes) doesn't auto-scroll
   * its container near the viewport edges, so a long list can't be reordered past what's already
   * visible. Scrolls the tree container a little on every `dragover` while the pointer is within
   * `edgePx` of its top/bottom edge. */
  private autoScrollTreeOnDragOver(event: DragEvent): void {
    const container = this.treeContainerRef?.nativeElement;
    if (!container) {
      return;
    }
    const edgePx = 50;
    const scrollStep = 14;
    const rect = container.getBoundingClientRect();
    const distanceFromTop = event.clientY - rect.top;
    const distanceFromBottom = rect.bottom - event.clientY;

    if (distanceFromTop < edgePx) {
      container.scrollTop -= scrollStep;
    } else if (distanceFromBottom < edgePx) {
      container.scrollTop += scrollStep;
    }
  }

  ngOnInit(): void {
    this.loadCategoryTree();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['clearSelection'] &&
      changes['clearSelection'].currentValue === true
    ) {
      this.clearTreeSelection();
    }
  }

  /**
   * Clear tree selection and emit null
   */
  clearTreeSelection(): void {
    this.selectedNode.set(null);
    this.categorySelect.emit(null);
    this.cdr.detectChanges();
  }

  /**
   * Handle search input changes
   */
  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.searchText.set(value);
    this.filterTreeData(value);
  }

  /**
   * Clear search
   */
  clearSearch(): void {
    this.searchText.set('');
    this.filterTreeData('');
  }

  /**
   * Filter tree data based on search text
   */
  private filterTreeData(searchText: string): void {
    if (!searchText.trim()) {
      this.filteredTreeData.set(this.treeData());
      return;
    }

    const filtered: TreeNode[] = [];
    this.treeData().forEach((node) => {
      const matchesSearch = node.label?.toLowerCase().includes(searchText);
      const children = node.children
        ? this.filterNodes(node.children, searchText)
        : [];

      if (matchesSearch || children.length > 0) {
        filtered.push({
          ...node,
          children: children.length > 0 ? children : undefined,
        });
      }
    });

    this.filteredTreeData.set(filtered);
  }

  /**
   * Recursively filter tree nodes
   */
  private filterNodes(nodes: TreeNode[], searchText: string): TreeNode[] {
    const filtered: TreeNode[] = [];

    nodes.forEach((node) => {
      const matchesSearch = node.label
        ?.toLowerCase()
        .includes(searchText.toLowerCase());
      const children = node.children
        ? this.filterNodes(node.children, searchText)
        : [];

      // Include node if it matches search OR if any of its descendants match
      if (matchesSearch || children.length > 0) {
        filtered.push({
          ...node,
          children: children.length > 0 ? children : undefined,
        });
      }
    });

    return filtered;
  }

  /**
   * Load the category tree data
   */
  private loadCategoryTree(): void {
    this.loading.set(true);

    // Use the same API call pattern as other components for consistency
    this.classifyService.getCategoryTree().subscribe({
      next: (categories: ClassifyModel[]) => {
        if (!categories || categories.length === 0) {
          this.treeData.set([]);
          this.filteredTreeData.set([]);
          this.loading.set(false);
          return;
        }

        const treeNodes = this.convertToTreeNodes(categories);
        this.treeData.set(treeNodes);
        this.filteredTreeData.set(treeNodes); // Initialize filtered data
        this.loading.set(false);

        // Force change detection
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('Sidebar: Error loading category tree:', error);
        this.loading.set(false);
        this.treeData.set([]);
        this.filteredTreeData.set([]);
      },
    });
  }

  /**
   * Convert category models to PrimeNG tree nodes
   * Handles both hierarchical (from getTree) and flat (from getList2) structures
   */
  private convertToTreeNodes(categories: ClassifyModel[]): TreeNode[] {
    if (!categories || categories.length === 0) {
      return [];
    }

    // Check if this is a hierarchical structure (from getTree API)
    if (this.isHierarchicalStructure(categories)) {
      return this.convertHierarchicalToTreeNodes(categories);
    }

    // Fall back to flat structure conversion (from getList2 API)
    return this.convertFlatToTreeNodes(categories);
  }

  /**
   * Check if the categories array has a hierarchical structure
   */
  private isHierarchicalStructure(categories: ClassifyModel[]): boolean {
    if (categories.length === 0) return false;

    // Check if the first category has children property
    const firstCategory = categories[0] as any;
    return firstCategory && firstCategory.children !== undefined;
  }

  /**
   * Convert hierarchical structure to PrimeNG tree nodes
   */
  private convertHierarchicalToTreeNodes(categories: any[]): TreeNode[] {
    const convertNode = (category: any): TreeNode => {
      const node: TreeNode = {
        key: category.id?.toString() || '',
        label: getCategoryDisplayName(category),
        data: category,
        expanded: false,
        selectable: true,
        leaf: category.children && category.children.length === 0,
        children: undefined,
      };


      // If category has children (subcategories), add them
      if (category.children && category.children.length > 0) {
        const childNodes = category.children.map((child: any) => convertNode(child));
        if (node.children) {
          // Merge with existing attribute children
          node.children = [...node.children, ...childNodes];
        } else {
          node.children = childNodes;
        }
        node.leaf = false;
      }

      return node;
    };

    const result = categories.map(convertNode);
    return result;
  }



  /**
   * Convert flat structure to PrimeNG tree nodes with proper hierarchy
   */
  private convertFlatToTreeNodes(categories: ClassifyModel[]): TreeNode[] {
    // Create a map for quick lookup
    const categoryMap = new Map<number, TreeNode>();
    const rootNodes: TreeNode[] = [];

    // First pass: create all nodes
    categories.forEach((category) => {
      const node: TreeNode = {
        key: category.id?.toString() || '',
        label: getCategoryDisplayName(category),
        data: category,
        icon: 'pi pi-folder',
        children: this.buildCategoryChildren(category),
        expanded: false,
        selectable: true,
        leaf: !category.attributeList || category.attributeList.length === 0,
      };

      categoryMap.set(category.id || 0, node);
    });

    // Second pass: build parent-child relationships
    categories.forEach((category) => {
      const node = categoryMap.get(category.id || 0);
      if (node) {
        if (!category.parentId || category.parentId === 0) {
          // Root node
          rootNodes.push(node);
        } else {
          // Child node
          const parentNode = categoryMap.get(category.parentId);
          if (parentNode) {
            if (!parentNode.children) {
              parentNode.children = [];
            }
            parentNode.children.push(node);
            parentNode.leaf = false;
          } else {
            // Parent not found, treat as root
            rootNodes.push(node);
          }
        }
      }
    });

    return rootNodes;
  }

  /**
   * Build children nodes for a category including attributes and their values
   */
  private buildCategoryChildren(
    category: ClassifyModel
  ): TreeNode[] | undefined {
    const children: TreeNode[] = [];

    // Add attributes as children with proper hierarchy
    if (category.attributeList && category.attributeList.length > 0) {
      category.attributeList.forEach((attribute) => {
        const attributeNode: TreeNode = {
          key: `${category.id}-attr-${attribute.code || attribute.name}`,
          label: `${attribute.name || 'Unnamed Attribute'}`,
          data: { ...attribute, type: 'attribute' },
          icon: 'pi pi-tag',
          children: this.buildAttributeValueNodes(attribute),
          expanded: false,
          selectable: false,
          leaf:
            !attribute.attributeValueList ||
            attribute.attributeValueList.length === 0,
        };
        children.push(attributeNode);
      });
    }

    return children.length > 0 ? children : undefined;
  }

  /**
   * Build nodes for attribute values with proper hierarchy
   */
  private buildAttributeValueNodes(attribute: any): TreeNode[] | undefined {
    if (
      !attribute.attributeValueList ||
      attribute.attributeValueList.length === 0
    ) {
      return undefined;
    }

    return attribute.attributeValueList.map((value: any, index: number) => ({
      key: `${attribute.code || attribute.name}-value-${index}`,
      label: `${value.value || 'Unnamed Value'}`,
      data: { ...value, type: 'attributeValue' },
      icon: 'pi pi-circle-fill',
      children: undefined, // No children for attribute values
      expanded: false,
      selectable: false,
      leaf: true, // Always leaf nodes
    }));
  }

  /**
   * Handle tree node selection
   */
  onNodeSelect(node: TreeNode): void {
    this.selectedNode.set(node);
    this.categorySelect.emit(node.data);
  }

  /**
   * Handle "All Categories" click
   */
  onAllCategoriesClick(): void {
    this.selectedNode.set(null);
    this.categorySelect.emit(null);
  }

  /** Find the node with the given key in a tree, to restore selection after a rebuild. */
  private findNodeByKey(nodes: TreeNode[], key: string | undefined): TreeNode | null {
    if (!key) {
      return null;
    }
    for (const node of nodes) {
      if (node.key === key) {
        return node;
      }
      if (node.children) {
        const found = this.findNodeByKey(node.children, key);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  /** Find the children array owned by the node with the given id (or the root array itself
   * when `parentId` is null), used to read the *original* sibling order out of a snapshot. */
  private findSiblingGroup(nodes: TreeNode[], parentId: number | null): TreeNode[] | null {
    if (parentId === null) {
      return nodes;
    }
    for (const node of nodes) {
      if ((node.data?.id ?? null) === parentId) {
        return node.children ?? [];
      }
      if (node.children) {
        const found = this.findSiblingGroup(node.children, parentId);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  /** Reset `treeData`/`filteredTreeData`/selection from a full-tree snapshot, reapplying the
   * current search filter so `filteredTreeData` never ends up holding a partial tree. */
  private restoreFromSnapshot(snapshot: TreeNode[], selectedKey: string | undefined): void {
    this.treeData.set(snapshot);
    this.filterTreeData(this.searchText());
    this.selectedNode.set(this.findNodeByKey(snapshot, selectedKey));
    this.cdr.detectChanges();
  }

  /**
   * Handle drag-and-drop reorder of a category among its siblings.
   *
   * A drop only ever submits a draft - the reorder isn't in effect until it's approved, so the
   * tree keeps showing today's real order right after the drop (snapping back), and only reflects
   * the change once the tree is reloaded post-approval. A toast confirms the draft was submitted.
   *
   * Swap-mode (same parent): when there's no *null* `sort` at or before the further of the 2
   * touched positions, a drop only trades the dragged node and the drop target's `sort` - no other
   * sibling is touched, re-indexed, or drafted. A duplicate (non-null) `sort` shared with some
   * other, unrelated sibling is left alone (it just ties and sorts by id - not broken enough to
   * justify dragging unrelated categories, and their open-draft locks, into this swap). If the 2
   * touched nodes are tied *with each other*, trading their identical values would be an invisible
   * no-op, so their own list positions are used instead - always distinct, still touches nobody
   * else. This works identically no matter which of PrimeNG's two drop gestures fired it (dropping
   * *between* rows or *onto* a node's body both resolve to the same `dropNode`), so there's no
   * need to special-case a body-drop as "nest as child" vs "insert after".
   *
   * Move-mode (different parent): a drop onto a node under a different parent moves the dragged
   * category into that parent's group instead - see `moveCategoryToNewParent`.
   *
   * A *null* `sort` is the one thing that actually breaks display order: the backend sorts nulls
   * last (see CategoryManageServiceImpl#selectList), so a null sibling sitting before the later of
   * the 2 touched positions would otherwise jump to the end of the list regardless of where it
   * visually sits. When one is found, the prefix from the start of the group through the later
   * touched position is re-indexed to sequential values - siblings after that point are untouched
   * and don't get a draft. Only siblings whose `sort` actually changes are drafted and checked for
   * an open draft - a sibling that merely sits inside the prefix but already holds its correct
   * index is left alone and can't block the drop. No cap on how large that prefix can get -
   * swapping should always work, however many siblings need fixing up.
   *
   * Always resolved against `treeData()` (the full, unfiltered tree), not `filteredTreeData()`:
   * during search, `filteredTreeData()` is a partial, copied view missing non-matching siblings,
   * which would both miscount a dirty-sort group and, if written back into `treeData`, corrupt
   * it permanently. `filteredTreeData` is regenerated from the updated `treeData` afterwards via
   * `filterTreeData`, so it stays consistent no matter whether search is active.
   */
  onCategoryDrop(event: TreeNodeDropEvent): void {
    if (!event.accept || !event.dragNode || !event.dropNode) {
      return;
    }

    const dragNode = event.dragNode;
    const dropNode = event.dropNode;
    const originalParentId: number | null = dragNode.data?.parentId ?? null;
    const dropNodeParentId: number | null = dropNode.data?.parentId ?? null;
    const dragId = dragNode.data?.id ?? null;
    const dropId = dropNode.data?.id ?? null;

    // Snapshot the full tree exactly as it is right now - including any earlier successful
    // swaps in this session - so PrimeNG's own drop side-effects (nesting/splicing the *live*
    // arrays) can be discarded and replaced with our own swap computed from this untouched copy.
    const preDropSnapshot = structuredClone(this.treeData());
    const selectedKey = this.selectedNode()?.key;

    event.accept();

    if (dragId === dropId) {
      // Dropped onto itself - nothing to do, just snap back.
      this.restoreFromSnapshot(preDropSnapshot, selectedKey);
      return;
    }

    if (dropNodeParentId !== originalParentId) {
      // Dropped onto a node under a different parent - move the dragged category into that
      // parent's group instead of reordering. The backend (validateDraft, called both when the
      // draft is created and again at Level 2 approval) already rejects cycles, dropping onto a
      // leaf category, and exceeding the max category depth, so there's no need to duplicate
      // that validation here.
      this.moveCategoryToNewParent(
        preDropSnapshot,
        selectedKey,
        originalParentId,
        dropNodeParentId,
        dragId,
        dropId,
      );
      return;
    }

    const siblings = this.findSiblingGroup(preDropSnapshot, originalParentId) ?? [];
    const dragIndex = siblings.findIndex((n) => (n.data?.id ?? null) === dragId);
    const dropIndex = siblings.findIndex((n) => (n.data?.id ?? null) === dropId);
    if (dragIndex === -1 || dropIndex === -1) {
      this.restoreFromSnapshot(preDropSnapshot, selectedKey);
      return;
    }

    // The backend sorts this list by `sort` ascending with NULLS LAST (see
    // CategoryManageServiceImpl#selectList), so any *null* sibling at or before the further of
    // the 2 touched positions would otherwise jump to the end regardless of where it visually
    // sits - that's the only thing that forces touching more than the 2 dragged/dropped nodes.
    // A sibling that merely shares a duplicate (non-null) `sort` with some other, unrelated
    // sibling is left alone - it just ties and sorts by id, which isn't broken enough to justify
    // dragging unrelated categories (and their open-draft locks) into this swap.
    const boundary = Math.max(dragIndex, dropIndex);
    const prefixSortValues = siblings.slice(0, boundary + 1).map((n) => n.data?.sort);
    const prefixDirty = prefixSortValues.some((s) => s == null);

    const draggedNode = siblings[dragIndex];
    const droppedNode = siblings[dropIndex];

    // Compute the post-swap order into a fresh array, not `siblings` itself - `siblings` aliases
    // a node's `children` (or the root array) inside `preDropSnapshot`, so mutating it here would
    // corrupt the very snapshot `restoreFromSnapshot` relies on to revert the UI if a check below
    // rejects the drop, making the reject path silently keep the swap instead of undoing it.
    const reordered = [...siblings];
    reordered[dragIndex] = droppedNode;
    reordered[dropIndex] = draggedNode;

    // Compute the draft batch before touching any node's data, so the open-draft check below can
    // look at exactly the nodes that will actually change - not every node merely inside the
    // repaired prefix. A sibling whose sort already matches its new index isn't part of this
    // update and shouldn't be able to block it just for sitting in the same prefix.
    let items: { id: number; sort: number }[];
    if (prefixDirty) {
      items = reordered
        .slice(0, boundary + 1)
        .map((node, i) => ({ node, sort: i }))
        .filter(({ node, sort }) => node.data?.sort !== sort)
        .map(({ node, sort }) => ({ id: Number(node.data?.id), sort }));
    } else if (draggedNode.data?.sort === droppedNode.data?.sort) {
      // The 2 touched nodes are tied with each other (and only each other - no null in the
      // prefix). Trading their identical `sort` values would be a no-op the user can't see, so
      // use their own list positions instead - always distinct from each other, and doesn't
      // require touching any other sibling.
      items = [
        { id: Number(draggedNode.data?.id), sort: dropIndex },
        { id: Number(droppedNode.data?.id), sort: dragIndex },
      ];
    } else {
      items = [
        { id: Number(draggedNode.data?.id), sort: Number(droppedNode.data?.sort) },
        { id: Number(droppedNode.data?.id), sort: Number(draggedNode.data?.sort) },
      ];
    }

    const itemIds = new Set(items.map((item) => item.id));
    const draftBlockers = siblings.filter((node) => itemIds.has(Number(node.data?.id)));
    if (draftBlockers.some((node) => node.data?.hasOpenDraft)) {
      this.restoreFromSnapshot(preDropSnapshot, selectedKey);
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant('goods.classify.messages.warning.reorder_open_draft'),
        life: 4000,
      });
      return;
    }

    // Don't apply the swap to the tree - the reorder isn't in effect until the draft is
    // approved, so the tree should keep showing today's real order until then. This also
    // discards PrimeNG's own default drop side-effect (it nests/splices the live nodes before
    // `onCategoryDrop` runs), snapping the tree back to exactly how it looked before the drag.
    this.restoreFromSnapshot(preDropSnapshot, selectedKey);

    this.classifyService.reorderCategories(originalParentId, items).subscribe({
      next: () => {
        // Mark the categories we just drafted as having an open draft locally - otherwise the
        // stale local data would let a second drag on the same node reach the API again, where
        // the backend's "one open draft per category" rule rejects it as an error instead of the
        // friendly warning the openDraft guard above is meant to show.
        this.markCategoriesHasOpenDraft(items.map((item) => item.id));
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('goods.classify.messages.success.title'),
          detail: this.translateService.instant('goods.classify.messages.success.reorder_success'),
          life: 4000,
        });
      },
      error: () => {
        // Persist failed - reload from server to discard the optimistic local change.
        this.loadCategoryTree();
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('goods.classify.messages.error.title'),
          detail: this.translateService.instant('goods.classify.messages.error.reorder_failed'),
          life: 5000,
        });
      },
    });
  }

  /**
   * Move a category to become a new sibling under a different parent (dropped onto a node
   * belonging to that parent). If the destination group's prefix up through the drop point has no
   * null `sort` (see onCategoryDrop for why null specifically matters), the dragged node just
   * takes the drop target's `sort` verbatim - the target itself and its other siblings are left
   * untouched, tying with the target is fine (self-heals on a later drag in that group). If a null
   * is present, that prefix is re-indexed to sequential values instead, touching only the siblings
   * whose `sort` actually changes (the dragged node is always one of them, since it needs a draft
   * for the parent change regardless of whether its new `sort` happens to match its old one). No
   * cap on how large that prefix can get. The old sibling group is otherwise untouched. The move
   * isn't in effect until the draft is approved, so the tree keeps showing today's real position
   * until then - only a toast confirms the draft was submitted.
   */
  private moveCategoryToNewParent(
    preDropSnapshot: TreeNode[],
    selectedKey: string | undefined,
    oldParentId: number | null,
    newParentId: number | null,
    dragId: number | null,
    dropId: number | null,
  ): void {
    const oldSiblings = this.findSiblingGroup(preDropSnapshot, oldParentId) ?? [];
    const newSiblings = this.findSiblingGroup(preDropSnapshot, newParentId) ?? [];
    const dragIndex = oldSiblings.findIndex((n) => (n.data?.id ?? null) === dragId);
    const dropIndex = newSiblings.findIndex((n) => (n.data?.id ?? null) === dropId);
    if (dragIndex === -1 || dropIndex === -1) {
      this.restoreFromSnapshot(preDropSnapshot, selectedKey);
      return;
    }

    const draggedNode = oldSiblings[dragIndex];
    if (draggedNode.data?.hasOpenDraft) {
      this.restoreFromSnapshot(preDropSnapshot, selectedKey);
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant('goods.classify.messages.warning.reorder_open_draft'),
        life: 4000,
      });
      return;
    }

    const dropNode = newSiblings[dropIndex];

    // Only a *null* sibling at or before the drop point actually needs fixing (see onCategoryDrop
    // for why) - a duplicate (non-null) `sort` shared with some other, unrelated sibling in the
    // destination group is left alone rather than dragging it (and its open-draft lock) into
    // this move.
    const destinationPrefixDirty = newSiblings.slice(0, dropIndex + 1).some((n) => n.data?.sort == null);

    let items: { id: number; sort: number }[];
    if (destinationPrefixDirty) {
      // Insert into a *copy* of the destination group, not `newSiblings` itself - `newSiblings`
      // aliases `preDropSnapshot`, so mutating it before the checks below would corrupt the very
      // snapshot the reject paths rely on to revert the UI (see onCategoryDrop for the same
      // issue).
      const reorderedNewSiblings = [...newSiblings];
      reorderedNewSiblings.splice(dropIndex, 0, draggedNode);
      items = reorderedNewSiblings
        .slice(0, dropIndex + 1)
        .map((node, i) => ({ node, sort: i }))
        .filter(({ node, sort }) => node.data?.sort !== sort || node === draggedNode)
        .map(({ node, sort }) => ({ id: Number(node.data?.id), sort }));
    } else {
      // Destination prefix is clean - the dragged node can just take the drop target's `sort`
      // verbatim. No other sibling needs touching (unlike a same-parent swap, the drop target
      // itself isn't being repositioned, so tying with it is fine - it'll self-heal the next time
      // someone drags within that group).
      items = [{ id: Number(dragId), sort: Number(dropNode.data?.sort) }];
    }

    const itemIds = new Set(items.map((item) => item.id));
    const draftBlockers = [draggedNode, ...newSiblings].filter((node) => itemIds.has(Number(node.data?.id)));
    if (draftBlockers.some((node) => node.data?.hasOpenDraft)) {
      this.restoreFromSnapshot(preDropSnapshot, selectedKey);
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant('goods.classify.messages.warning.reorder_open_draft'),
        life: 4000,
      });
      return;
    }

    // Don't apply the move to the tree - the reorder isn't in effect until the draft is
    // approved, so the tree should keep showing today's real order until then. This also
    // discards PrimeNG's own default drop side-effect (it nests/splices the live nodes before
    // `onCategoryDrop` runs), snapping the tree back to exactly how it looked before the drag.
    this.restoreFromSnapshot(preDropSnapshot, selectedKey);

    this.classifyService.reorderCategories(newParentId, items).subscribe({
      next: () => {
        this.markCategoriesHasOpenDraft(items.map((item) => item.id));
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('goods.classify.messages.success.title'),
          detail: this.translateService.instant('goods.classify.messages.success.reorder_success'),
          life: 4000,
        });
      },
      error: () => {
        // Persist failed - reload from server to discard the optimistic local move.
        this.loadCategoryTree();
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('goods.classify.messages.error.title'),
          detail: this.translateService.instant('goods.classify.messages.error.reorder_failed'),
          life: 5000,
        });
      },
    });
  }

  /** Flag categories as having an open draft in the local tree right after we create one for
   * them - keeps the `hasOpenDraft` guard above accurate for a second drag before the next full
   * reload, instead of letting a stale `false` through to the API only for the backend's "one
   * open draft per category" rule to reject it. */
  private markCategoriesHasOpenDraft(ids: number[]): void {
    const idSet = new Set(ids);
    const markNodes = (nodes: TreeNode[]): TreeNode[] =>
      nodes.map((node) => {
        const children = node.children ? markNodes(node.children) : node.children;
        if (idSet.has(node.data?.id)) {
          return { ...node, data: { ...node.data, hasOpenDraft: true }, children };
        }
        return children !== node.children ? { ...node, children } : node;
      });

    this.treeData.set(markNodes(this.treeData()));
    this.filterTreeData(this.searchText());
  }
}
